/**
 * Allow-list for images the renderer may load (M5, ADR-0009 #2, docs/08 section 5): ONLY approved,
 * sanitized derivatives served same-origin by the API at `/api/v1/media/<key>.(png|webp)`. Anything
 * else (another origin, a protocol-relative URL, javascript:/data:/blob:, credentials, a query or
 * fragment, dot segments, percent-encoding, other API paths, other extensions, control characters
 * or non-ASCII look-alikes) is refused before any request is made; the sign or billboard then keeps
 * its generated fallback. The URL must already be in canonical form (no normalisation is trusted).
 */
export const MEDIA_PATH_PREFIX = "/api/v1/media/";
export const MAX_MEDIA_URL_LENGTH = 500;

/** Random keys, optionally under up to three plain folders; PNG or WebP derivatives only. */
const MEDIA_PATH = /^\/api\/v1\/media\/(?:[A-Za-z0-9_-]{1,128}\/){0,3}[A-Za-z0-9_-]{1,128}\.(?:png|webp)$/;
/** Printable ASCII only (no space, no control characters, no Unicode look-alikes)... */
const PRINTABLE_ASCII = /^[!-~]+$/;
/** ...and none of the characters a canonical media URL never contains (backslash, quotes, brackets...). */
const FORBIDDEN_CHARS = /[\\"'<>`{}|^[\]]/;

export type MediaUrlRejection =
  | "not_string"
  | "empty"
  | "too_long"
  | "bad_characters"
  | "bad_origin"
  | "unparseable"
  | "bad_scheme"
  | "credentials"
  | "cross_origin"
  | "query_or_fragment"
  | "not_canonical"
  | "bad_path";

export type MediaUrlCheck = { ok: true; url: string } | { ok: false; reason: MediaUrlRejection };

/**
 * Validate `raw` against the page origin (`location.origin`). Accepts a root-relative path
 * (`/api/v1/media/k.png`) or the same absolute same-origin URL; returns the absolute URL to fetch.
 */
export function checkMediaUrl(raw: unknown, origin: string): MediaUrlCheck {
  if (typeof raw !== "string") return { ok: false, reason: "not_string" };
  if (raw.length === 0) return { ok: false, reason: "empty" };
  if (raw.length > MAX_MEDIA_URL_LENGTH) return { ok: false, reason: "too_long" };
  if (!PRINTABLE_ASCII.test(raw) || FORBIDDEN_CHARS.test(raw)) return { ok: false, reason: "bad_characters" };
  let base: URL;
  try {
    base = new URL(origin);
  } catch {
    return { ok: false, reason: "bad_origin" };
  }
  if ((base.protocol !== "https:" && base.protocol !== "http:") || base.origin === "null") return { ok: false, reason: "bad_origin" };
  let url: URL;
  try {
    url = new URL(raw, base.origin);
  } catch {
    return { ok: false, reason: "unparseable" };
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return { ok: false, reason: "bad_scheme" };
  if (url.username !== "" || url.password !== "" || raw.includes("@")) return { ok: false, reason: "credentials" };
  if (url.origin !== base.origin) return { ok: false, reason: "cross_origin" };
  if (raw.includes("?") || raw.includes("#") || url.search !== "" || url.hash !== "") return { ok: false, reason: "query_or_fragment" };
  // Canonical form only: a relative path must be exactly the resolved path, an absolute URL exactly
  // the resolved href (no dot segments, no default ports, no case folding, no encoding tricks).
  const relative = raw.startsWith("/") && !raw.startsWith("//");
  if (relative ? raw !== url.pathname : raw !== url.href) return { ok: false, reason: "not_canonical" };
  if (!MEDIA_PATH.test(url.pathname)) return { ok: false, reason: "bad_path" };
  return { ok: true, url: url.href };
}

/** The absolute URL to load, or null when `raw` is not an allowed same-origin media URL. */
export function safeMediaUrl(raw: unknown, origin: string): string | null {
  const r = checkMediaUrl(raw, origin);
  return r.ok ? r.url : null;
}

/** The page origin in the browser; "null" (refuses every URL) elsewhere. */
export function pageOrigin(): string {
  return typeof location !== "undefined" && typeof location.origin === "string" ? location.origin : "null";
}
